import React, { useState, useEffect, useRef } from 'react';
import { supportApi } from '../../services/supportApi';
import { mqttSupportService } from '../../services/mqttSupportService';
import { FiPaperclip, FiX, FiFile, FiImage, FiMessageSquare } from 'react-icons/fi';
import CannedResponsePicker from '../CannedResponsePicker';
import { appendCannedResponseContent, useCannedResponseShortcuts } from '../../hooks/useCannedResponseShortcuts';
import './SupportChat.css';

interface AIMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

interface ChatMessage {
  id: number;
  senderId: number;
  senderName: string;
  receiverId: number;
  receiverName: string;
  content: string;
  fileUrl?: string;
  fileType?: string;
  createdAt: string;
  isMine: boolean;
  isRead: boolean;
}

interface SupportRequest {
  id: number;
  requesterId: number;
  requesterName: string;
  requesterPhone: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  aiConversationHistory?: AIMessage[];
  hasAiHistory: boolean;
  chatRoomRoomId: string;
}

interface SupportChatProps {
  requestId: number;
  roomId: string;
  onBack: () => void;
}

const SupportChat: React.FC<SupportChatProps> = ({ requestId, roomId, onBack }) => {
  const [request, setRequest] = useState<SupportRequest | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mqttConnected, setMqttConnected] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [adminId, setAdminId] = useState<number | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showCannedResponsePicker, setShowCannedResponsePicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);

  useCannedResponseShortcuts(newMessage, setNewMessage);
  const connectionCallbackRef = useRef<((connected: boolean) => void) | null>(null);
  const currentRoomRef = useRef<string | null>(null);
  const messageCallbackRef = useRef<((message: any) => void) | null>(null);
  const connectionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoReconnectAttemptsRef = useRef<number>(0);

  useEffect(() => {
    // Load request first to get admin ID, then load messages
    const initialize = async () => {
      await loadRequest();
      // Load messages after request is loaded (adminId should be set)
      await loadMessages();
    };

    initialize();
    setupMQTT();

    // Periodic connection check and auto-reconnect
    connectionCheckIntervalRef.current = setInterval(() => {
      const isConnected = mqttSupportService.isConnectedToBroker();
      if (isConnected !== mqttConnected) {
        console.log('🔄 [Support Chat] Connection status updated:', isConnected ? 'Connected ✅' : 'Disconnected ❌');
        setMqttConnected(isConnected);

        if (isConnected) {
          // Reset reconnect attempts on successful connection
          autoReconnectAttemptsRef.current = 0;
          setReconnecting(false);
          // If reconnected and we have a roomId, resubscribe
          if (roomId && (!currentRoomRef.current || currentRoomRef.current !== roomId)) {
            console.log('🔄 [Support Chat] Reconnected, resubscribing to room:', roomId);
            subscribeToRoom(roomId);
          }
        } else {
          // Not connected - try to reconnect automatically
          console.log('⚠️ [Support Chat] Connection lost, attempting automatic reconnect...');
          handleReconnect();
        }
      }
    }, 3000); // Check every 3 seconds for faster reconnection

    return () => {
      // Cleanup: unsubscribe from the room when component unmounts or roomId changes
      if (currentRoomRef.current) {
        console.log('🧹 [Support Chat] Cleaning up MQTT subscription for room:', currentRoomRef.current);
        mqttSupportService.unsubscribeFromSupportRoom(currentRoomRef.current);
        currentRoomRef.current = null;
      }

      // Cleanup: remove connection status callback
      if (connectionCallbackRef.current) {
        mqttSupportService.offConnectionStatusChange(connectionCallbackRef.current);
        connectionCallbackRef.current = null;
      }

      // Clear connection check interval
      if (connectionCheckIntervalRef.current) {
        clearInterval(connectionCheckIntervalRef.current);
        connectionCheckIntervalRef.current = null;
      }
    };
  }, [requestId, roomId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Helper function to handle incoming messages
  const handleIncomingMessage = (message: any) => {
    console.log('📨 [Support Chat] New message received via MQTT:', message);
    setMessages((prev) => {
      // Check if message already exists by ID
      const existsById = prev.some(m => m.id === message.id);
      if (existsById) {
        console.log('⚠️ [Support Chat] Message already exists (by ID), skipping:', message.id);
        return prev;
      }
      // Also check if it's a duplicate by content and timestamp (in case IDs differ)
      const existsByContent = prev.some(m =>
        m.content === message.content &&
        m.senderId === message.senderId &&
        Math.abs(new Date(m.createdAt).getTime() - new Date(message.createdAt).getTime()) < 1000
      );
      if (existsByContent) {
        console.log('⚠️ [Support Chat] Duplicate message detected (by content), skipping');
        return prev;
      }
      console.log('✅ [Support Chat] Adding new message from MQTT:', message.id);
      return [...prev, message];
    });
  };

  // Helper function to subscribe to a room
  const subscribeToRoom = (roomIdToSubscribe: string) => {
    // Unsubscribe from previous room if different
    if (currentRoomRef.current && currentRoomRef.current !== roomIdToSubscribe) {
      console.log('🔄 [Support Chat] Unsubscribing from previous room:', currentRoomRef.current);
      mqttSupportService.unsubscribeFromSupportRoom(currentRoomRef.current);
    }

    // Store the message callback
    messageCallbackRef.current = handleIncomingMessage;

    // Subscribe to the new room
    mqttSupportService.subscribeToSupportRoom(roomIdToSubscribe, messageCallbackRef.current);
    currentRoomRef.current = roomIdToSubscribe;
    console.log('✅ [Support Chat] Subscribed to room:', roomIdToSubscribe);
  };

  const setupMQTT = () => {
    const token = localStorage.getItem('adminToken');

    if (!token) {
      console.warn('⚠️ [Support Chat] No auth token found, cannot connect to MQTT');
      return;
    }

    console.log('🔌 [Support Chat] Setting up MQTT connection...');
    console.log('   - Room ID:', roomId);
    console.log('   - Token available:', !!token);

    // Register connection status callback only once
    if (!connectionCallbackRef.current) {
      connectionCallbackRef.current = (connected: boolean) => {
        console.log('📡 [Support Chat] MQTT connection status changed:', connected ? 'Connected ✅' : 'Disconnected ❌');
        setMqttConnected(connected);

        // When connected, subscribe to the current chat room
        if (connected && roomId) {
          subscribeToRoom(roomId);
        }
      };

      mqttSupportService.onConnectionStatusChange(connectionCallbackRef.current);
    }

    // Connect to MQTT (this will reuse existing connection if already connected)
    mqttSupportService.connect(token);

    // Initial check after a short delay
    setTimeout(() => {
      const isConnected = mqttSupportService.isConnectedToBroker();
      console.log('📡 [Support Chat] Initial MQTT connection status:', isConnected ? 'Connected ✅' : 'Disconnected ❌');
      setMqttConnected(isConnected);

      // If already connected, subscribe immediately
      if (isConnected && roomId) {
        subscribeToRoom(roomId);
      }
    }, 1000);

    // If roomId changed and we're already connected, subscribe to new room
    if (currentRoomRef.current !== roomId && mqttSupportService.isConnectedToBroker() && roomId) {
      subscribeToRoom(roomId);
    }
  };

  // Manual reconnect handler
  const handleReconnect = () => {
    const token = localStorage.getItem('adminToken');

    if (!token) {
      console.warn('⚠️ [Support Chat] No auth token found, cannot reconnect to MQTT');
      return;
    }

    if (reconnecting) {
      console.log('⏳ [Support Chat] Reconnection already in progress...');
      return;
    }

    setReconnecting(true);
    autoReconnectAttemptsRef.current += 1;

    console.log('🔄 [Support Chat] Manual reconnect attempt #', autoReconnectAttemptsRef.current);

    // Try to connect (this will reuse existing connection if already connected)
    mqttSupportService.connect(token);

    // Wait a moment and check if connection was successful
    setTimeout(() => {
      const isConnected = mqttSupportService.isConnectedToBroker();
      setMqttConnected(isConnected);
      setReconnecting(false);

      if (isConnected && roomId) {
        subscribeToRoom(roomId);
        console.log('✅ [Support Chat] Reconnected successfully!');
      } else {
        console.warn('⚠️ [Support Chat] Reconnection attempt failed, will retry automatically');
      }
    }, 2000);
  };

  const loadRequest = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No authentication token found');
        return;
      }

      const data = await supportApi.getRequestById(token, requestId);
      setRequest(data);

      // Get admin ID from assigned admin or decode from token
      if (data.assignedAdminId) {
        setAdminId(data.assignedAdminId);
      } else {
        // Try to decode admin ID from token (JWT payload)
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.userId || payload.id || payload.sub) {
            setAdminId(payload.userId || payload.id || payload.sub);
          }
        } catch (e) {
          console.warn('Could not decode admin ID from token');
        }
      }
    } catch (error: any) {
      console.error('Failed to load request:', error);
      alert(error.message || 'Failed to load request details');
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No authentication token found');
        return;
      }

      // Get chat messages from the room
      const response = await fetch(`${supportApi.getBaseUrl()}/chat/room/${roomId}/messages?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load messages');
      }

      const data = await response.json();

      // Ensure messages have correct isMine flag based on admin ID
      // This ensures admin messages appear on the right
      const processedMessages = data.map((msg: ChatMessage) => {
        if (adminId) {
          // Override isMine based on actual admin ID comparison
          return {
            ...msg,
            isMine: msg.senderId === adminId
          };
        }
        return msg;
      });

      setMessages(processedMessages);
    } catch (error: any) {
      console.error('Failed to load messages:', error);
      alert(error.message || 'Failed to load chat messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !request || sending) return;

    try {
      setSending(true);
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No authentication token found');
        return;
      }

      console.log('📤 [Support Chat] Sending message...', mqttConnected ? '(MQTT connected)' : '(MQTT disconnected)');

      let message: any = null;
      try {
        if (selectedFile) {
          const formData = new FormData();
          formData.append('receiverId', request.requesterId.toString());
          if (newMessage.trim()) formData.append('content', newMessage.trim());
          if (requestId) formData.append('requestId', requestId.toString());
          formData.append('file', selectedFile);

          message = await supportApi.sendChatMessageWithFile(token, formData);
          console.log('✅ [Support Chat] Message with file sent via HTTP');
        } else {
          // Send via HTTP API (primary)
          message = await supportApi.sendChatMessage(token, request.requesterId, newMessage);
          console.log('✅ [Support Chat] Message sent via HTTP');
        }
      } catch (httpError) {
        console.warn('⚠️ [Support Chat] HTTP send failed, trying MQTT fallback...', httpError);

        if (mqttConnected && roomId && !selectedFile) {
          // Fallback: Send via MQTT publish (Only if no file is selected, as MQTT doesn't support file blobs natively here)
          const mqttMessage = {
            id: Date.now(), // Temporary ID
            senderId: adminId,
            senderName: 'Admin',
            receiverId: request.requesterId,
            receiverName: request.requesterName,
            content: newMessage,
            fileUrl: null,
            fileType: null,
            createdAt: new Date().toISOString(),
            isMine: true,
            isRead: false
          };

          mqttSupportService.publishChatMessage(roomId, mqttMessage);
          message = mqttMessage;
          console.log('✅ [Support Chat] Message sent via MQTT fallback');
        } else {
          throw httpError; // Rethrow if MQTT also unavailable
        }
      }

      // Only add message to UI immediately if MQTT is NOT connected or if we used MQTT fallback
      // If MQTT is connected and we used HTTP, it will receive the message via MQTT callback
      if (!mqttConnected || (message && message.isMine && !message.id.toString().startsWith('1'))) {
        setMessages((prev) => {
          const exists = prev.some(m => m.id === message.id || (m.content === message.content && m.createdAt === message.createdAt));
          if (exists) return prev;
          return [...prev, message];
        });
      }

      setNewMessage('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setTimeout(() => scrollToBottom(), 100);
    } catch (error: any) {
      console.error('❌ [Support Chat] Failed to send message:', error);
      alert(error.message || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    if (!window.confirm('Mark this support request as resolved?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('No authentication token found');
        return;
      }

      await supportApi.resolveRequest(token, requestId);
      alert('Request marked as resolved');
      await loadRequest();
    } catch (error: any) {
      console.error('Failed to resolve request:', error);
      alert(error.message || 'Failed to resolve request');
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
    // Also scroll container to bottom as fallback
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInsertCannedResponse = (content: string) => {
    setNewMessage(prev => appendCannedResponseContent(prev, content));
    setTimeout(() => messageInputRef.current?.focus(), 100);
  };

  if (loading || !request) {
    return (
      <div className="support-chat-loading">
        <div className="spinner"></div>
        <p>Loading chat...</p>
      </div>
    );
  }

  return (
    <div className="support-chat">
      <div className="chat-header">
        <div className="header-left">
          <button className="back-button" onClick={onBack}>
            Back to Requests
          </button>
          <div className="header-info">
            <h2>{request.subject}</h2>
            <p>Chatting with: {request.requesterName} ({request.requesterPhone})</p>
          </div>
        </div>
        <div className="header-right">
          <div className={`mqtt-status-indicator ${mqttConnected ? 'online' : 'offline'}`}>
            <span className="status-dot"></span>
            {mqttConnected ? 'Online' : reconnecting ? 'Reconnecting...' : 'Offline'}
          </div>
          {!mqttConnected && autoReconnectAttemptsRef.current >= 2 && (
            <button
              className="btn-reconnect"
              onClick={handleReconnect}
              disabled={reconnecting}
              title="Reconnect to MQTT"
            >
              {reconnecting ? 'Reconnecting...' : 'Reconnect'}
            </button>
          )}
          {request.status !== 'RESOLVED' && (
            <button className="btn-resolve" onClick={handleResolve}>
              Mark as Resolved
            </button>
          )}
          {request.status === 'RESOLVED' && (
            <span className="resolved-badge">Resolved</span>
          )}
        </div>
      </div>

      {/* Single Column Layout - Live Agent Chat */}
      <div className="chat-columns-container">
        <div className="live-chat-column">
          <div className="live-chat-messages" ref={messagesContainerRef}>
            {messages.length === 0 ? (
              <div className="no-messages">
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map((message) => {
                // Determine if message is from admin
                // Check if senderId matches adminId, or use isMine from backend
                const isAdminMessage = adminId ? message.senderId === adminId : message.isMine;

                return (
                  <div
                    key={message.id}
                    className={`message ${isAdminMessage ? 'my-message' : 'other-message'} ${message.fileUrl ? 'has-attachment' : ''}`}
                  >
                    <div className="message-header">
                      <span className="sender-name">{message.senderName}</span>
                      <span className="message-time">
                        {new Date(message.createdAt).toLocaleTimeString()}
                      </span>
                    </div>

                    {message.fileUrl && (
                      <div className="message-attachment-container">
                        {message.fileType === 'image' || message.fileUrl.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i) ? (
                          <a href={message.fileUrl.startsWith('http') ? message.fileUrl : `${supportApi.getBaseUrl().replace('/api', '')}${message.fileUrl}`} target="_blank" rel="noopener noreferrer">
                            <img
                              src={message.fileUrl.startsWith('http') ? message.fileUrl : `${supportApi.getBaseUrl().replace('/api', '')}${message.fileUrl}`}
                              alt="Attachment"
                              className="chat-attachment-image"
                            />
                          </a>
                        ) : (
                          <a href={message.fileUrl.startsWith('http') ? message.fileUrl : `${supportApi.getBaseUrl().replace('/api', '')}${message.fileUrl}`} target="_blank" rel="noopener noreferrer" className="chat-attachment-link">
                            {React.createElement(FiFile as any, { className: "attachment-icon" })}
                            <span>Download Attachment</span>
                          </a>
                        )}
                      </div>
                    )}

                    {message.content && (
                      <div className="message-content">{message.content}</div>
                    )}

                    {isAdminMessage && message.isRead && (
                      <div className="read-indicator">Read</div>
                    )}
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />

            {/* Scroll to bottom button */}
            {showScrollButton && (
              <button
                className="scroll-to-bottom"
                onClick={scrollToBottom}
                title="Scroll to bottom"
              >
                Scroll to Bottom
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Message Input */}
      <div className="message-input-container">
        {selectedFile && (
          <div className="selected-file-preview">
            <div className="file-info">
              {selectedFile.type.startsWith('image/') ? React.createElement(FiImage as any) : React.createElement(FiFile as any)}
              <span className="file-name">{selectedFile.name}</span>
            </div>
            <button className="remove-file-btn" onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} title="Remove file">
              {React.createElement(FiX as any)}
            </button>
          </div>
        )}
        <div className="message-input">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                const file = e.target.files[0];
                if (file.size > 10 * 1024 * 1024) {
                  alert("File size exceeds 10MB limit.");
                  e.target.value = '';
                  return;
                }
                setSelectedFile(file);
              }
            }}
            accept="image/*,.pdf,.doc,.docx,.txt,audio/*"
          />
          <button
            type="button"
            className="btn-canned"
            onClick={() => setShowCannedResponsePicker(true)}
            disabled={sending}
            title="Insert canned response (type /shortcut + space)"
          >
            {React.createElement(FiMessageSquare as any)}
          </button>
          <button
            className="btn-attach"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            title="Attach file"
          >
            {React.createElement(FiPaperclip as any)}
          </button>
          <input
            ref={messageInputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="EN shortcut + space or AR (e.g. /greet_ar ) or type freely"
            disabled={sending}
          />
          <button
            onClick={handleSendMessage}
            disabled={sending || (!newMessage.trim() && !selectedFile)}
            className="send-button"
          >
            {sending ? (
              <>
                <span className="spinner-small"></span> Sending...
              </>
            ) : (
              'Send'
            )}
          </button>
        </div>
      </div>
      <CannedResponsePicker
        anchorEl={messageInputRef.current}
        open={showCannedResponsePicker}
        onClose={() => setShowCannedResponsePicker(false)}
        onSelect={handleInsertCannedResponse}
        placement="top"
      />
      {!mqttConnected && autoReconnectAttemptsRef.current >= 2 && (
        <div className="connection-warning">
          <div className="warning-content">
            <p>⚠️ MQTT connection lost. Messages may not be received in real-time.</p>
            <button
              className="btn-reconnect-inline"
              onClick={handleReconnect}
              disabled={reconnecting}
            >
              {reconnecting ? 'Reconnecting...' : 'Reconnect Now'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportChat;

